import React, { useMemo, useState } from "react";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  ChevronLeft, 
  ChevronRight,
  Check,
  Circle,
  Briefcase,
  Shield,
  Wallet,
  TrendingUp,
  Home,
  Zap,
  Handshake,
  ClipboardList,
  Star,
  FileSearch,
  Target,
  Users,
  Edit3,
  Calendar
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import type { BeratungSeite } from "@/hooks/useBeratungSeiten";
import type { BeratungModul } from "@/hooks/useBeratungModule";

const ICON_MAP: Record<string, React.ElementType> = {
  Handshake,
  ClipboardList,
  Star,
  FileSearch,
  Target,
  Briefcase,
  Shield,
  Wallet,
  Zap,
  TrendingUp,
  Home,
  Users,
};

interface BeratungSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  modules: BeratungModul[];
  selectedModuleIds: string[];
  allSeiten: BeratungSeite[];
  currentPageIndex: number;
  onPageSelect: (index: number) => void;
  formValues: Record<string, any>;
  canNavigate?: boolean;
  // Session info for header
  sessionName?: string;
  sessionDate?: Date;
  onSessionNameChange?: (name: string) => void;
  onSessionDateChange?: (date: Date | undefined) => void;
}

export function BeratungSidebar({
  isOpen,
  onToggle,
  modules,
  selectedModuleIds,
  allSeiten,
  currentPageIndex,
  onPageSelect,
  formValues,
  canNavigate = true,
  sessionName,
  sessionDate,
  onSessionNameChange,
  onSessionDateChange,
}: BeratungSidebarProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(sessionName || '');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleSaveName = () => {
    if (editedName.trim() && onSessionNameChange) {
      onSessionNameChange(editedName.trim());
    }
    setIsEditingName(false);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date && onSessionDateChange) {
      onSessionDateChange(date);
    }
    setIsDatePickerOpen(false);
  };
  // Group pages by module
  const pagesByModule = useMemo(() => {
    const grouped: Record<string, BeratungSeite[]> = {};
    
    allSeiten.forEach(seite => {
      if (!grouped[seite.modul_id]) {
        grouped[seite.modul_id] = [];
      }
      grouped[seite.modul_id].push(seite);
    });
    
    return grouped;
  }, [allSeiten]);

  // Calculate progress per module
  const moduleProgress = useMemo(() => {
    const progress: Record<string, { completed: number; total: number }> = {};
    
    Object.entries(pagesByModule).forEach(([modulId, seiten]) => {
      let completed = 0;
      seiten.forEach(seite => {
        // Check if any form field for this page is filled
        const layout = seite.berater_layout;
        if (layout.fields) {
          const filledFields = layout.fields.filter(f => formValues[f.id] !== undefined && formValues[f.id] !== '');
          if (filledFields.length > 0) {
            completed++;
          }
        } else if (layout.items) {
          // For rankings/checklists
          const key = layout.type === 'ranking' ? `ranking_${seite.id}` : seite.id;
          if (formValues[key]) {
            completed++;
          }
        }
      });
      progress[modulId] = { completed, total: seiten.length };
    });
    
    return progress;
  }, [pagesByModule, formValues]);

  // Get current page's module ID
  const currentSeite = allSeiten[currentPageIndex];
  const currentModulId = currentSeite?.modul_id;

  // Get selected modules in order
  const selectedModules = useMemo(() => 
    modules.filter(m => selectedModuleIds.includes(m.id)),
    [modules, selectedModuleIds]
  );

  // Default open accordion items (current module)
  const defaultOpenModules = currentModulId ? [currentModulId] : [];

  if (!isOpen) {
    return (
      <div className="w-10 border-r bg-muted/30 flex flex-col items-center py-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="mb-4"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Navigation öffnen</TooltipContent>
        </Tooltip>
        
        {/* Mini module indicators - click navigates to module, dots shown for current module */}
        <div className="space-y-1">
          {selectedModules.map((modul) => {
            const IconComponent = (modul.icon && ICON_MAP[modul.icon]) || Briefcase;
            const isActive = modul.id === currentModulId;
            const pages = pagesByModule[modul.id] || [];
            // Find first page of this module to navigate to
            const firstPageIndex = allSeiten.findIndex(s => s.modul_id === modul.id);
            
            return (
              <div key={modul.id} className="flex flex-col items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        // Navigate to first page of this module
                        if (firstPageIndex >= 0) {
                          onPageSelect(firstPageIndex);
                        }
                      }}
                      className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all",
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted hover:bg-muted-foreground/10"
                      )}
                    >
                      <IconComponent className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    <p>{modul.name}</p>
                  </TooltipContent>
                </Tooltip>
                
                {/* Page dots - shown automatically for the current module */}
                {isActive && pages.length > 0 && (
                  <div className="flex flex-col items-center gap-1 py-1">
                    {pages.map((seite) => {
                      const globalIndex = allSeiten.findIndex(s => s.id === seite.id);
                      const isPageActive = globalIndex === currentPageIndex;
                      const isDisabled = !canNavigate && globalIndex > currentPageIndex;
                      
                      return (
                        <Tooltip key={seite.id}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => !isDisabled && onPageSelect(globalIndex)}
                              disabled={isDisabled}
                              className={cn(
                                "w-3 h-3 rounded-full transition-all",
                                isDisabled 
                                  ? "bg-muted-foreground/20 cursor-not-allowed"
                                  : isPageActive 
                                    ? "bg-primary" 
                                    : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                              )}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={8}>
                            <p>{seite.titel}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 border-r bg-muted/30 flex flex-col">
      {/* Header with Session Info */}
      <div className="border-b px-3 py-2.5 space-y-1">
        <div className="flex items-center justify-between">
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Beratungsname..."
              className="h-6 text-sm flex-1 mr-2"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') {
                  setEditedName(sessionName || '');
                  setIsEditingName(false);
                }
              }}
              onBlur={handleSaveName}
            />
          ) : (
            <button
              onClick={() => {
                setEditedName(sessionName || '');
                setIsEditingName(true);
              }}
              className="flex items-center gap-1 font-semibold text-sm hover:text-primary transition-colors group text-left flex-1 min-w-0 mr-2"
              title="Beratung umbenennen"
            >
              <span className="line-clamp-2 break-words">{sessionName || 'Digitale Beratung'}</span>
              <Edit3 className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
            </button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-7 w-7 shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Date */}
        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title="Datum ändern"
            >
              <Calendar className="h-3 w-3" />
              {sessionDate ? format(sessionDate, "dd. MMMM yyyy", { locale: de }) : "Datum wählen"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={sessionDate}
              onSelect={handleDateSelect}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Module Accordion */}
      <ScrollArea className="flex-1">
        <Accordion
          type="multiple"
          defaultValue={defaultOpenModules}
          className="px-2 py-2"
        >
          {selectedModules.map((modul) => {
            const IconComponent = (modul.icon && ICON_MAP[modul.icon]) || Briefcase;
            const pages = pagesByModule[modul.id] || [];
            const progress = moduleProgress[modul.id] || { completed: 0, total: 0 };
            const progressPercent = progress.total > 0 
              ? (progress.completed / progress.total) * 100 
              : 0;

            return (
              <AccordionItem key={modul.id} value={modul.id} className="border-none">
                <AccordionTrigger className="py-2 px-2 hover:no-underline hover:bg-muted rounded-lg">
                  <div className="flex items-center gap-2 flex-1 text-left">
                    <div className={cn(
                      "w-7 h-7 rounded-md flex items-center justify-center",
                      modul.id === currentModulId 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted"
                    )}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{modul.name}</p>
                      <Progress value={progressPercent} className="h-1 mt-1" />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-2">
                  <div className="space-y-1 pl-2">
                    {pages.map((seite, idx) => {
                      const globalIndex = allSeiten.findIndex(s => s.id === seite.id);
                      const isActive = globalIndex === currentPageIndex;
                      const isCompleted = false; // Could check form values here
                      
                      const isDisabled = !canNavigate && globalIndex > currentPageIndex;
                      
                      return (
                        <button
                          key={seite.id}
                          onClick={() => !isDisabled && onPageSelect(globalIndex)}
                          disabled={isDisabled}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all text-sm",
                            isDisabled 
                              ? "opacity-50 cursor-not-allowed text-muted-foreground"
                              : isActive 
                                ? "bg-primary/10 text-primary font-medium" 
                                : "hover:bg-muted text-muted-foreground hover:text-foreground"
                          )}
                        >
                          {isCompleted ? (
                            <Check className="h-3.5 w-3.5 text-chart-5 flex-shrink-0" />
                          ) : isActive ? (
                            <div className="w-3.5 h-3.5 rounded-full bg-primary flex-shrink-0" />
                          ) : (
                            <Circle className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          <span className="truncate">{seite.titel}</span>
                        </button>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </ScrollArea>

      {/* Footer Stats */}
      <div className="border-t p-3">
        <div className="text-xs text-muted-foreground">
          Seite {currentPageIndex + 1} von {allSeiten.length}
        </div>
        <Progress 
          value={((currentPageIndex + 1) / allSeiten.length) * 100} 
          className="h-1.5 mt-2" 
        />
      </div>
    </div>
  );
}
